import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table';
import type { ContactEmployeeWithUser } from '@/api/contact-employee/contact-employee.types';
import { useGetAllUsers } from '@/api/user/user.hooks';
import { useAdminChangePassword } from '@/api/user/user.hooks';
import {
  useSendStocklist,
  useEmailSubscriptions,
  useUpsertStocklistSubscription,
  useDeleteStocklistSubscription,
} from '@/api/email-subscriptions/email-subscriptions.hooks';
import type { EmailSubscription } from '@/api/email-subscriptions/email-subscriptions.types';
import {
  minutesToTimeString,
  timeStringToMinutes,
} from '@/api/email-subscriptions/email-subscriptions.mappers';
import { DsSearchInput, DsKv, DsButton, DsInput, DsModal, DsPill, DsAlert } from '@/modules/internal-v2/ds';
import { DsTable } from '../../shared/ds-table.component';
import { IfPermitted } from '../../shared/if-permitted.component';
import { PERMISSIONS } from '@/constants/permissions';
import { ACCOUNT_TYPE } from '@/constants/account-type';
import { usePermissions } from '@/hooks/use-permissions';

const col = createColumnHelper<ContactEmployeeWithUser>();

const columns = [
  col.accessor('firstName', {
    header: 'Vorname',
    size: 110,
    cell: (info) => (
      <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{info.getValue()}</span>
    ),
  }),
  col.accessor('lastName', {
    header: 'Nachname',
    size: 130,
    cell: (info) => (
      <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{info.getValue()}</span>
    ),
  }),
  col.accessor('position', {
    header: 'Position',
    size: 140,
    meta: { flex: true },
    cell: (info) => {
      const v = info.getValue();
      return v ? (
        <span style={{ color: 'var(--ink-2)' }}>{v}</span>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      );
    },
  }),
  col.accessor('email', {
    header: 'E-Mail',
    size: 200,
    meta: { flex: true },
    cell: (info) => {
      const v = info.getValue();
      return v ? (
        <a
          href={`mailto:${v}`}
          style={{ color: 'var(--accent-ink)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {v}
        </a>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      );
    },
    enableSorting: false,
  }),
  col.accessor('phone1', {
    header: 'Telefon',
    size: 130,
    cell: (info) => {
      const v = info.getValue();
      return v ? (
        <a
          href={`tel:${v}`}
          style={{ color: 'var(--accent-ink)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {v}
        </a>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      );
    },
    enableSorting: false,
  }),
  col.accessor('phone2', {
    header: 'Mobil',
    size: 130,
    cell: (info) => {
      const v = info.getValue();
      return v ? (
        <a
          href={`tel:${v}`}
          style={{ color: 'var(--accent-ink)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {v}
        </a>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      );
    },
    enableSorting: false,
  }),
  col.accessor('eosUserEmail', {
    header: 'EOS User',
    size: 180,
    cell: (info) => {
      const v = info.getValue();
      return v ? (
        <span className="ds-pill pos" style={{ fontSize: 'var(--fs-xs)' }}>{v}</span>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      );
    },
  }),
];

/** Selectable weekdays for the stocklist subscription (Mon–Fri, ISO ints). */
const STOCKLIST_DAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Mo' },
  { value: 2, label: 'Di' },
  { value: 3, label: 'Mi' },
  { value: 4, label: 'Do' },
  { value: 5, label: 'Fr' },
];

/** Renders ISO weekday ints as a short German day list. */
function formatStocklistDays(daysOfWeek: number[] | null): string {
  if (!daysOfWeek || daysOfWeek.length === 0) return '—';
  return [...daysOfWeek]
    .sort((a, b) => a - b)
    .map((d) => STOCKLIST_DAYS.find((day) => day.value === d)?.label ?? d)
    .join(', ');
}

const PAGE_SIZE = 50;

interface BpContactsTabProps {
  contacts: ContactEmployeeWithUser[];
  isPending: boolean;
}

export function BpContactsTab({ contacts, isPending }: BpContactsTabProps) {
  const navigate = useNavigate();
  const { bpId, contactId } = useParams<{ bpId: string; contactId: string }>();
  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'lastName', desc: false }]);
  const [page, setPage] = useState(0);

  const contactsBase = `/i/business-partners/${bpId}/kontakte`;
  const selected = contactId
    ? (contacts.find((c) => String(c.id) === contactId) ?? null)
    : null;

  const filtered = useMemo(() => {
    if (!search) return contacts;
    const q = search.toLowerCase();
    return contacts.filter(
      (c) =>
        c.firstName?.toLowerCase().includes(q) ||
        c.lastName?.toLowerCase().includes(q) ||
        (c.email?.toLowerCase().includes(q)) ||
        (c.position?.toLowerCase().includes(q)) ||
        (c.phone1?.includes(q)) ||
        (c.phone2?.includes(q)) ||
        (c.eosUserEmail?.toLowerCase().includes(q)),
    );
  }, [contacts, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(
    () => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filtered, page],
  );

  const table = useReactTable({
    data: paged,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => String(row.id),
    enableSortingRemoval: false,
  });

  return (
    <div className="bp-tab-host">
      <div className="bp-orders-toolbar">
        <DsSearchInput
          className="bp-orders-search"
          value={search}
          onSearch={setSearch}
          debounce={0}
          placeholder="Name, E-Mail, Position…"
        />
        <div className="bp-orders-pager">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>‹</button>
          <span>Seite <b style={{ color: 'var(--ink)' }}>{page + 1}</b> / {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>›</button>
        </div>
      </div>
      <DsTable
        table={table}
        fixedLayout
        selectedRowId={contactId ?? undefined}
        onRowClick={(c) => navigate(`${contactsBase}/${c.id}`)}
        emptyMessage={isPending ? 'Laden…' : 'Keine Ansprechpartner gefunden.'}
      />

      {selected && (
        <ContactOverlay contact={selected} onClose={() => navigate(contactsBase)} />
      )}
    </div>
  );
}

function ContactOverlay({ contact, onClose }: { contact: ContactEmployeeWithUser; onClose: () => void }) {
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [subModalOpen, setSubModalOpen] = useState(false);

  const { has } = usePermissions();
  const canManageInternalUsers = has(PERMISSIONS.USERS_MANAGE);
  const canManageSubscriptions = has(PERMISSIONS.EMAIL_SUBSCRIPTIONS_MANAGE);

  // The linked account's id + type come from the BP-scoped contact payload, so
  // a sales rep can manage their own customers' portal accounts without the
  // internal `USERS_MANAGE` user list. The full user detail (isAdmin, …) is an
  // optional enrichment that only internal-user admins may load.
  const { data: users } = useGetAllUsers({
    queryKey: ['users'],
    enabled: !!contact.eosUserId && canManageInternalUsers,
  });
  const user = useMemo(
    () => users?.find((u) => u.id === contact.eosUserId) ?? null,
    [users, contact.eosUserId],
  );

  const {
    data: subscriptions,
    isPending: subsPending,
    isError: subsError,
  } = useEmailSubscriptions(contact.id, canManageSubscriptions);
  const stocklistSub = useMemo(
    () => subscriptions?.find((s) => s.topic === 'stocklist') ?? null,
    [subscriptions],
  );

  return (
    <div className="bp-overlay">
      <div className="bp-overlay-bar">
        <button className="bp-cmd-back" onClick={onClose}>← Zurück</button>
        <span className="bp-overlay-title">{contact.firstName} {contact.lastName}</span>
        {contact.position && <span className="bp-overlay-sub">{contact.position}</span>}
      </div>
      <div className="bp-overlay-body ds-scroll">
        <div className="bp-overlay-section">
          <DsKv
            keyWidth={120}
            entries={[
              {
                label: 'E-Mail',
                value: contact.email ? (
                  <a href={`mailto:${contact.email}`} style={{ color: 'var(--accent-ink)' }}>
                    {contact.email}
                  </a>
                ) : null,
                missing: !contact.email,
              },
              {
                label: 'Telefon',
                value: contact.phone1 ? (
                  <a href={`tel:${contact.phone1}`} style={{ color: 'var(--accent-ink)', fontFamily: 'var(--font-mono)' }}>
                    {contact.phone1}
                  </a>
                ) : null,
                missing: !contact.phone1,
              },
              {
                label: 'Mobil',
                value: contact.phone2 ? (
                  <a href={`tel:${contact.phone2}`} style={{ color: 'var(--accent-ink)', fontFamily: 'var(--font-mono)' }}>
                    {contact.phone2}
                  </a>
                ) : null,
                missing: !contact.phone2,
              },
              {
                label: 'EOS User',
                value: contact.eosUserEmail ? (
                  <span className="ds-pill pos" style={{ fontSize: 'var(--fs-xs)' }}>
                    {contact.eosUserEmail}
                  </span>
                ) : null,
                missing: !contact.eosUserEmail,
              },
            ]}
          />
        </div>

        <IfPermitted permission={PERMISSIONS.EMAIL_SUBSCRIPTIONS_MANAGE}>
        <div className="bp-overlay-section">
          <div className="bp-overlay-section-label">Aktionen</div>
          <DsButton
            size="small"
            onClick={() => setSendModalOpen(true)}
            disabled={!contact.email}
          >
            Bestandsliste senden
          </DsButton>
          {!contact.email && (
            <div
              style={{
                marginTop: 6,
                fontSize: 'var(--fs-xs)',
                color: 'var(--ink-4)',
              }}
            >
              Keine E-Mail-Adresse hinterlegt — Versand nicht möglich.
            </div>
          )}
        </div>

        <div className="bp-overlay-section">
          <div className="bp-overlay-section-label">Bestandsliste-Abo</div>
          {subsPending ? (
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-4)' }}>
              Laden…
            </div>
          ) : subsError ? (
            <DsAlert severity="error" style={{ marginBottom: 4 }}>
              Abo konnte nicht geladen werden.
            </DsAlert>
          ) : stocklistSub ? (
            <DsKv
              keyWidth={120}
              entries={[
                {
                  label: 'Status',
                  value: (
                    <DsPill status={stocklistSub.enabled ? 'pos' : 'muted'}>
                      {stocklistSub.enabled ? 'Aktiv' : 'Inaktiv'}
                    </DsPill>
                  ),
                },
                {
                  label: 'Versandtage',
                  value: formatStocklistDays(stocklistSub.daysOfWeek),
                },
                {
                  label: 'Uhrzeit',
                  value:
                    stocklistSub.timeOfDay != null
                      ? `${minutesToTimeString(stocklistSub.timeOfDay)} Uhr`
                      : '—',
                },
                {
                  label: 'Zuletzt gesendet',
                  value: stocklistSub.lastSentAt
                    ? new Date(stocklistSub.lastSentAt).toLocaleString('de-DE')
                    : null,
                  missing: !stocklistSub.lastSentAt,
                },
              ]}
            />
          ) : (
            <div
              style={{
                fontSize: 'var(--fs-sm)',
                color: 'var(--ink-4)',
                marginBottom: 4,
              }}
            >
              Kein Bestandslisten-Abo eingerichtet.
            </div>
          )}
          <DsButton
            size="small"
            onClick={() => setSubModalOpen(true)}
            style={{ marginTop: 8 }}
          >
            {stocklistSub ? 'Abo bearbeiten' : 'Abo einrichten'}
          </DsButton>
        </div>
        </IfPermitted>

        {contact.eosUserId && (
          <div className="bp-overlay-section">
            <div className="bp-overlay-section-label">EOS Account</div>
            <DsKv
              keyWidth={120}
              entries={[
                {
                  label: 'User-ID',
                  value: (
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>
                      {contact.eosUserId}
                    </span>
                  ),
                },
                {
                  label: 'Kontotyp',
                  value: (
                    <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {contact.eosUserAccountType && (
                        <DsPill status="muted">{contact.eosUserAccountType}</DsPill>
                      )}
                      {user?.isAdmin && (
                        <DsPill status="pos">Administrator</DsPill>
                      )}
                    </span>
                  ),
                },
              ]}
            />
            {/* Internal accounts need USERS_MANAGE; external (customer) portal
                accounts need BUSINESS_PARTNERS_VIEW + BP scope — the backend
                enforces the same rule and scopes the reset to the rep's BPs. */}
            <IfPermitted
              permission={
                contact.eosUserAccountType === ACCOUNT_TYPE.INTERNAL
                  ? PERMISSIONS.USERS_MANAGE
                  : PERMISSIONS.BUSINESS_PARTNERS_VIEW
              }
            >
              <DsButton
                size="small"
                onClick={() => setPwModalOpen(true)}
                style={{ marginTop: 8 }}
              >
                Passwort ändern
              </DsButton>
            </IfPermitted>
          </div>
        )}

        {(contact.remarks1 || contact.remarks2) && (
          <div className="bp-overlay-section">
            <div className="bp-overlay-section-label">Bemerkungen</div>
            <pre>{[contact.remarks1, contact.remarks2].filter(Boolean).join('\n')}</pre>
          </div>
        )}
      </div>

      <SendStocklistModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        contact={contact}
      />

      <EditSubscriptionModal
        open={subModalOpen}
        onClose={() => setSubModalOpen(false)}
        contact={contact}
        subscription={stocklistSub}
      />

      {contact.eosUserId && (
        <ChangePasswordModal
          open={pwModalOpen}
          onClose={() => setPwModalOpen(false)}
          userId={contact.eosUserId}
          email={contact.eosUserEmail ?? ''}
        />
      )}
    </div>
  );
}

function SendStocklistModal({
  open,
  onClose,
  contact,
}: {
  open: boolean;
  onClose: () => void;
  contact: ContactEmployeeWithUser;
}) {
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    msg: string;
  } | null>(null);
  const [email, setEmail] = useState(contact.email ?? '');
  const [synced, setSynced] = useState({ id: contact.id, open });
  const { mutate, isPending } = useSendStocklist();

  // Reset the recipient field to the contact's email each time the modal opens.
  if (synced.id !== contact.id || synced.open !== open) {
    setSynced({ id: contact.id, open });
    if (open) setEmail(contact.email ?? '');
  }

  const handleClose = () => {
    setFeedback(null);
    onClose();
  };

  const trimmedEmail = email.trim();

  const handleSend = () => {
    setFeedback(null);
    mutate(
      { contactEmployeeId: contact.id, email: trimmedEmail || undefined },
      {
        onSuccess: (data) => {
          setFeedback({
            type: 'success',
            msg: data.message ?? 'Bestandsliste wurde gesendet.',
          });
        },
        onError: (error) => {
          const msg =
            (error.response?.data as { message?: string } | undefined)
              ?.message ?? 'Fehler beim Senden der Bestandsliste.';
          setFeedback({ type: 'error', msg });
        },
      },
    );
  };

  return (
    <DsModal
      open={open}
      onClose={handleClose}
      title="Bestandsliste senden"
      description="Aktuelle Bestandsliste mit den Preisen der Preisliste des Geschäftspartners senden."
      footer={
        <>
          <DsButton onClick={handleClose}>Schließen</DsButton>
          <DsButton
            variant="primary"
            onClick={handleSend}
            disabled={isPending || !trimmedEmail}
          >
            {isPending ? 'Senden…' : 'Jetzt senden'}
          </DsButton>
        </>
      }
    >
      {feedback && (
        <DsAlert severity={feedback.type} style={{ marginBottom: 8 }}>
          {feedback.msg}
        </DsAlert>
      )}
      <p
        style={{
          margin: '0 0 12px',
          color: 'var(--ink-2)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        {contact.firstName} {contact.lastName} erhält die aktuelle
        Bestandsliste. Die Empfänger-Adresse kann bei Bedarf überschrieben
        werden.
      </p>
      <label
        style={{
          display: 'block',
          marginBottom: 4,
          color: 'var(--ink-2)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        Empfänger E-Mail
      </label>
      <DsInput
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="empfaenger@example.com"
        style={{ width: '100%' }}
      />
      {!contact.email && (
        <p
          style={{
            margin: '8px 0 0',
            color: 'var(--ink-2)',
            fontSize: 'var(--fs-sm)',
          }}
        >
          Für diesen Ansprechpartner ist keine E-Mail hinterlegt — bitte eine
          Adresse eingeben.
        </p>
      )}
    </DsModal>
  );
}

function EditSubscriptionModal({
  open,
  onClose,
  contact,
  subscription,
}: {
  open: boolean;
  onClose: () => void;
  contact: ContactEmployeeWithUser;
  subscription: EmailSubscription | null;
}) {
  const upsert = useUpsertStocklistSubscription();
  const remove = useDeleteStocklistSubscription();
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    msg: string;
  } | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [time, setTime] = useState('08:00');

  // Re-seed the form from the subscription each time the modal opens or the
  // selected contact changes.
  const [synced, setSynced] = useState({ id: contact.id, open });
  if (synced.id !== contact.id || synced.open !== open) {
    setSynced({ id: contact.id, open });
    if (open) {
      setFeedback(null);
      setEnabled(subscription?.enabled ?? false);
      setDays(subscription?.daysOfWeek ?? [1, 2, 3, 4, 5]);
      setTime(
        subscription?.timeOfDay != null
          ? minutesToTimeString(subscription.timeOfDay)
          : '08:00',
      );
    }
  }

  const toggleDay = (day: number) =>
    setDays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort((a, b) => a - b),
    );

  const busy = upsert.isPending || remove.isPending;
  const invalid = enabled && days.length === 0;

  const handleSave = () => {
    setFeedback(null);
    upsert.mutate(
      {
        contactEmployeeId: contact.id,
        body: {
          enabled,
          daysOfWeek: days,
          timeOfDay: timeStringToMinutes(time),
        },
      },
      {
        onSuccess: () =>
          setFeedback({
            type: 'success',
            msg: 'Abo-Einstellungen gespeichert.',
          }),
        onError: () =>
          setFeedback({ type: 'error', msg: 'Fehler beim Speichern.' }),
      },
    );
  };

  const handleDelete = () => {
    setFeedback(null);
    remove.mutate(contact.id, {
      onSuccess: () => onClose(),
      onError: () =>
        setFeedback({ type: 'error', msg: 'Fehler beim Löschen des Abos.' }),
    });
  };

  return (
    <DsModal
      open={open}
      onClose={onClose}
      title="Bestandsliste-Abo"
      description={`Versandplan für ${contact.firstName} ${contact.lastName}.`}
      footer={
        <>
          {subscription && (
            <DsButton
              onClick={handleDelete}
              disabled={busy}
              style={{ marginRight: 'auto' }}
            >
              Abo löschen
            </DsButton>
          )}
          <DsButton onClick={onClose}>Schließen</DsButton>
          <DsButton
            variant="primary"
            onClick={handleSave}
            disabled={busy || invalid}
          >
            {upsert.isPending ? 'Speichern…' : 'Speichern'}
          </DsButton>
        </>
      }
    >
      {feedback && (
        <DsAlert severity={feedback.type} style={{ marginBottom: 8 }}>
          {feedback.msg}
        </DsAlert>
      )}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          color: 'var(--ink)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Bestandsliste-Abo aktiv
      </label>

      {enabled && (
        <>
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                marginBottom: 6,
                color: 'var(--ink-2)',
                fontSize: 'var(--fs-sm)',
              }}
            >
              Versandtage
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {STOCKLIST_DAYS.map((day) => {
                const active = days.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDay(day.value)}
                    className={`ds-pill ${active ? 'pos' : 'muted'}`}
                    style={{
                      cursor: 'pointer',
                      border: 'none',
                      fontFamily: 'inherit',
                      fontSize: 'var(--fs-xs)',
                    }}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
            {invalid && (
              <div
                style={{
                  marginTop: 6,
                  color: 'var(--ink-4)',
                  fontSize: 'var(--fs-xs)',
                }}
              >
                Mindestens ein Versandtag muss gewählt sein.
              </div>
            )}
          </div>

          <div style={{ marginTop: 14 }}>
            <div
              style={{
                marginBottom: 6,
                color: 'var(--ink-2)',
                fontSize: 'var(--fs-sm)',
              }}
            >
              Uhrzeit
            </div>
            <DsInput
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              style={{ width: 140 }}
            />
          </div>
        </>
      )}

      {!contact.email && (
        <p
          style={{
            margin: '12px 0 0',
            color: 'var(--ink-2)',
            fontSize: 'var(--fs-sm)',
          }}
        >
          Hinweis: Für diesen Ansprechpartner ist keine E-Mail hinterlegt — die
          Bestandsliste kann erst nach Eintragen einer Adresse versendet werden.
        </p>
      )}
    </DsModal>
  );
}

function ChangePasswordModal({ open, onClose, userId, email }: { open: boolean; onClose: () => void; userId: string; email: string }) {
  const [password, setPassword] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const { mutate, isPending } = useAdminChangePassword();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!password.trim()) return;
    setFeedback(null);
    mutate(
      { id: userId, newPassword: password },
      {
        onSuccess: () => {
          setFeedback({ type: 'success', msg: 'Passwort wurde geändert.' });
          setPassword('');
        },
        onError: () => {
          setFeedback({ type: 'error', msg: 'Fehler beim Ändern des Passworts.' });
        },
      },
    );
  };

  const handleClose = () => {
    setPassword('');
    setFeedback(null);
    onClose();
  };

  return (
    <DsModal
      open={open}
      onClose={handleClose}
      title="Passwort ändern"
      description={`Neues Passwort für ${email} setzen.`}
      footer={
        <>
          <DsButton onClick={handleClose}>Abbrechen</DsButton>
          <DsButton
            variant="primary"
            type="submit"
            form="bp-change-pw"
            disabled={isPending || !password.trim()}
          >
            {isPending ? 'Speichern…' : 'Speichern'}
          </DsButton>
        </>
      }
    >
      <form id="bp-change-pw" onSubmit={handleSubmit}>
        {feedback && (
          <DsAlert severity={feedback.type} style={{ marginBottom: 8 }}>
            {feedback.msg}
          </DsAlert>
        )}
        <DsInput
          type="password"
          placeholder="Neues Passwort"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          style={{ width: '100%' }}
        />
      </form>
    </DsModal>
  );
}
